<template>
  <section class="candidates-page">
    <div class="page-header">
      <p class="eyebrow">Phase 2 Tooling</p>
      <h2>Verified Candidates</h2>
      <p>
        Generate full-package ESP candidates for one saved scenario. Every
        displayable candidate must survive `run_sizing_calculation` and include
        a trace ID.
      </p>
    </div>

    <Card>
      <template #title>Generate Verified Candidates</template>
      <template #content>
        <div class="form-grid">
          <label>
            Scenario ID
            <InputText v-model="scenarioId" placeholder="scenario uuid" />
            <small>
              UUID for one saved sizing scenario. From
              `/sizings/:sizingId/scenarios/:scenarioId/...`, use the ID after
              `/scenarios/`.
            </small>
          </label>
          <label>
            Minimum Candidates
            <InputNumber
              v-model="minimumCandidates"
              :min="1"
              :max="25"
              show-buttons
            />
            <small
              >Backend clamps this to 1-25 and caps total calculation
              attempts.</small
            >
          </label>
        </div>

        <div class="full-package-note">
          <strong>Full package required:</strong>
          <span
            v-for="type in requiredPackageTypes"
            :key="type"
            class="equipment-chip"
          >
            {{ type }}
          </span>
        </div>

        <Button
          label="Generate Candidates"
          class="p-button-success mt-3"
          :loading="isGenerating"
          :disabled="!scenarioId"
          @click="runCandidateGeneration"
        />
      </template>
    </Card>

    <div v-if="error" class="error-banner">{{ error }}</div>

    <Card v-if="candidateResult" class="mt-4">
      <template #title>Candidate Generation Results</template>
      <template #subtitle>
        {{ candidateResult.displayableCandidates.length }} displayable,
        {{ candidateResult.rejectedCandidates.length }} rejected,
        {{ candidateResult.blockingReasons.length }} blockers
      </template>
      <template #content>
        <div class="candidate-summary">
          <div>
            <strong>Baseline trace:</strong>
            {{ candidateResult.baselineTraceId || "none" }}
          </div>
          <div>
            <strong>Calculation attempts:</strong>
            {{ candidateResult.attemptedCandidateCount || 0 }}
          </div>
          <div class="catalog-counts">
            <strong>Catalog counts:</strong>
            <span
              v-for="[type, count] in catalogCountEntries"
              :key="type"
              class="count-chip"
              :class="{ empty: count === 0 }"
            >
              {{ type }}: {{ count }}
            </span>
          </div>
        </div>

        <div
          v-if="candidateResult.rankingStatus === 'disabled'"
          class="warning-banner"
        >
          {{
            candidateResult.rankingMessage ||
            "Grouped ranking options are disabled."
          }}
        </div>

        <Card v-if="candidateResult.recommendedOption" class="recommended-card">
          <template #title>Recommended Validation Option</template>
          <template #subtitle>
            {{ laneLabel(candidateResult.recommendedOption.lane) }} rank #{{
              candidateResult.recommendedOption.rank
            }}
            · Score {{ candidateResult.recommendedOption.score }} · Trace
            {{ candidateResult.recommendedOption.traceId }}
          </template>
          <template #content>
            <div class="score-breakdown">
              <span
                v-for="[name, score] in Object.entries(
                  candidateResult.recommendedOption.scoreBreakdown
                )"
                :key="name"
                class="score-chip"
              >
                {{ name }}: {{ score }}
              </span>
            </div>
          </template>
        </Card>

        <div v-if="showEmptyRankingState" class="warning-banner">
          No grouped ranking options were returned. Check blockers, rejected
          candidates, and feature flags before validating ranking output.
        </div>

        <AiDosOptionLanes :lane-entries="optionLaneEntries" />

        <DataTable
          :value="candidateResult.displayableCandidates"
          responsive-layout="scroll"
        >
          <Column field="traceId" header="Trace ID" />
          <Column field="status" header="Status">
            <template #body="{ data }">
              <Tag
                :value="data.status"
                :severity="statusSeverity(data.status)"
              />
            </template>
          </Column>
          <Column header="Warnings">
            <template #body="{ data }">{{ data.warnings.length }}</template>
          </Column>
          <Column header="Hard Failures">
            <template #body="{ data }">{{ data.hardFailures.length }}</template>
          </Column>
          <Column header="Package Materials">
            <template #body="{ data }">
              <div class="material-list">
                <span
                  v-for="[type, id] in materialEntries(data.candidatePackage)"
                  :key="type"
                  class="material-chip"
                >
                  {{ type }}: {{ id }}
                </span>
              </div>
            </template>
          </Column>
        </DataTable>

        <Panel
          v-if="candidateResult.blockingReasons.length"
          header="Blocking Reasons"
          class="mt-3"
        >
          <AdminJsonViewer :value="candidateResult.blockingReasons" />
        </Panel>

        <Panel
          v-if="candidateResult.candidateFallbacks?.length"
          header="Reused Baseline Components"
          class="mt-3"
        >
          <p class="muted-copy">
            AI DOS reused existing non-primary equipment where material search
            returned no replacement candidates. Displayed candidates are still
            calculation-verified.
          </p>
          <AdminJsonViewer :value="candidateResult.candidateFallbacks" />
        </Panel>

        <Panel
          v-if="candidateResult.rejectedCandidates.length"
          header="Rejected Candidates"
          class="mt-3"
        >
          <DataTable
            :value="candidateResult.rejectedCandidates"
            responsive-layout="scroll"
          >
            <Column field="rejectionReason" header="Reason" />
            <Column field="traceId" header="Trace ID">
              <template #body="{ data }">{{ data.traceId || "none" }}</template>
            </Column>
            <Column header="Hard Failures">
              <template #body="{ data }">{{
                data.hardFailures?.length || 0
              }}</template>
            </Column>
            <Column header="Details">
              <template #body="{ data }">
                <AdminJsonViewer
                  :value="rejectionDetails(data)"
                  :deep="2"
                  class="inline-json-viewer"
                />
              </template>
            </Column>
          </DataTable>
        </Panel>
      </template>
    </Card>

    <Panel v-if="candidateResult" header="Raw Candidate JSON" class="mt-4">
      <div class="artifact-actions">
        <Button
          label="Copy Candidate JSON"
          class="p-button-text"
          @click="copyJson(candidateResult)"
        />
      </div>
      <AdminJsonViewer :value="candidateResult" />
    </Panel>
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useToast } from "primevue/usetoast";

import {
  generateCandidates,
  type CandidateGenerationResponse,
  type CandidateResult,
  type RankedCandidateOption
} from "@/libs/aiDos/adminApi";
import {
  copyJsonToClipboard,
  errorMessage,
  laneLabel,
  REQUIRED_CANDIDATE_EQUIPMENT_TYPES,
  statusSeverity
} from "@/libs/aiDos/adminShared";
import AiDosOptionLanes from "./AiDosOptionLanes.vue";
import AdminJsonViewer from "./AdminJsonViewer.vue";

const toast = useToast();
const requiredPackageTypes = [...REQUIRED_CANDIDATE_EQUIPMENT_TYPES];
const scenarioId = ref("");
const minimumCandidates = ref(10);
const isGenerating = ref(false);
const error = ref<string | null>(null);
const candidateResult = ref<CandidateGenerationResponse | null>(null);

const catalogCountEntries = computed(() =>
  Object.entries(candidateResult.value?.catalogCounts || {})
);
const optionLaneEntries = computed(
  () =>
    Object.entries(candidateResult.value?.options || {}) as Array<
      ["performance" | "runlife" | "cost", RankedCandidateOption[]]
    >
);
const showEmptyRankingState = computed(
  () =>
    Boolean(candidateResult.value) &&
    candidateResult.value?.rankingStatus !== "disabled" &&
    optionLaneEntries.value.every(([, options]) => options.length === 0) &&
    !candidateResult.value?.blockingReasons.length
);

const runCandidateGeneration = async () => {
  isGenerating.value = true;
  error.value = null;
  try {
    candidateResult.value = await generateCandidates({
      scenarioId: scenarioId.value,
      minimumCandidates: minimumCandidates.value || 10
    });
  } catch (err) {
    error.value = errorMessage(err);
  } finally {
    isGenerating.value = false;
  }
};

const copyJson = (value: unknown) => copyJsonToClipboard(toast, value);

const materialEntries = (
  candidatePackage: CandidateResult["candidatePackage"]
) => Object.entries(candidatePackage.metadata?.materialIds || {});

const rejectionDetails = (candidate: CandidateResult) => ({
  hardFailures: candidate.hardFailures || [],
  warnings: candidate.warnings || [],
  materialIds: candidate.candidatePackage?.metadata?.materialIds || {}
});
</script>

<style scoped lang="scss">
.page-header {
  max-width: 58rem;
  margin-bottom: 1rem;

  h2 {
    margin: 0 0 0.5rem;
    font-size: 2rem;
  }

  p:last-child {
    color: var(--text-color-secondary);
    line-height: 1.5;
  }
}

.eyebrow {
  color: var(--red-500);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  margin: 0 0 0.25rem;
  text-transform: uppercase;
}

.form-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));
}

label {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-weight: 700;

  small {
    color: var(--text-color-secondary);
    font-size: 0.82rem;
    font-weight: 400;
    line-height: 1.35;
  }
}

.error-banner {
  background: var(--red-50);
  border: 1px solid var(--red-200);
  border-radius: 0.5rem;
  color: var(--red-700);
  margin: 1rem 0;
  padding: 1rem;
}

.warning-banner {
  background: var(--yellow-50);
  border: 1px solid var(--yellow-200);
  border-radius: 0.5rem;
  color: var(--yellow-900);
  margin: 1rem 0;
  padding: 1rem;
}

.recommended-card {
  margin: 1rem 0;
}

.candidate-summary {
  display: grid;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.full-package-note,
.catalog-counts,
.material-list,
.score-breakdown {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.full-package-note {
  color: var(--text-color-secondary);
  font-size: 0.88rem;
  margin-top: 0.85rem;
}

.muted-copy {
  color: var(--text-color-secondary);
  margin: 0 0 0.75rem;
}

.equipment-chip,
.count-chip,
.material-chip,
.score-chip {
  border: 1px solid var(--surface-400);
  border-radius: 999px;
  color: var(--text-color);
  display: inline-flex;
  font-size: 0.78rem;
  line-height: 1.2;
  padding: 0.2rem 0.5rem;
}

.count-chip.empty {
  background: var(--red-50);
  border-color: var(--red-200);
  color: var(--red-700);
}

.material-list {
  max-width: 38rem;
}

.material-chip {
  max-width: 18rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.artifact-actions {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.json-output,
.inline-json {
  background: #111827;
  border: 1px solid #374151;
  color: #f9fafb;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
  overflow: auto;
  white-space: pre;
}

.json-output {
  border-radius: 0.5rem;
  font-size: 0.875rem;
  line-height: 1.45;
  max-height: 28rem;
  padding: 1rem;
}

.inline-json {
  border-radius: 0.375rem;
  font-size: 0.78rem;
  line-height: 1.35;
  margin: 0;
  max-height: 10rem;
  max-width: 32rem;
  padding: 0.65rem;
}
</style>
